import type { FC } from 'react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PaginationProps {
    page: number
    pageCount: number
    hrefFor: (page: number) => string
}

const pagesToShow = (page: number, count: number): Array<number | 'gap'> => {
    if (count <= 7) return Array.from({ length: count }, (_, index) => index + 1)

    const pages = new Set([1, count, page - 1, page, page + 1])
    const sorted = Array.from(pages).filter(value => value >= 1 && value <= count).sort((a, b) => a - b)
    const result: Array<number | 'gap'> = []

    sorted.forEach((value, index) => {
        if (index > 0 && value - sorted[index - 1] > 1) result.push('gap')
        result.push(value)
    })

    return result
}

export const Pagination: FC<PaginationProps> = ({ page, pageCount, hrefFor }) => {
    if (pageCount <= 1) return null

    const itemClass = 'flex h-9 min-w-9 items-center justify-center rounded-lg px-3 text-sm font-medium transition-colors'

    return (
        <nav aria-label='Pagination' className='flex items-center justify-center gap-1'>
            {page > 1 ? (
                <Link href={hrefFor(page - 1)} scroll className={cn(itemClass, 'gap-1 hover:bg-muted')} rel='prev'>
                    <ChevronLeft className='size-4' />
                    <span className='hidden sm:inline'>Previous</span>
                </Link>
            ) : (
                <span className={cn(itemClass, 'gap-1 text-muted-foreground/50')} aria-hidden='true'>
                    <ChevronLeft className='size-4' />
                    <span className='hidden sm:inline'>Previous</span>
                </span>
            )}

            {pagesToShow(page, pageCount).map((value, index) => value === 'gap' ? (
                <span key={`gap-${index}`} className='px-1 text-muted-foreground'>…</span>
            ) : (
                <Link
                    key={value}
                    href={hrefFor(value)}
                    aria-current={value === page ? 'page' : undefined}
                    className={cn(itemClass, value === page ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
                >
                    {value}
                </Link>
            ))}

            {page < pageCount ? (
                <Link href={hrefFor(page + 1)} className={cn(itemClass, 'gap-1 hover:bg-muted')} rel='next'>
                    <span className='hidden sm:inline'>Next</span>
                    <ChevronRight className='size-4' />
                </Link>
            ) : (
                <span className={cn(itemClass, 'gap-1 text-muted-foreground/50')} aria-hidden='true'>
                    <span className='hidden sm:inline'>Next</span>
                    <ChevronRight className='size-4' />
                </span>
            )}
        </nav>
    )
}
